import React, { useEffect, useRef } from 'react';
import { Animated, Easing, StyleSheet, View } from 'react-native';
import theme from 'shared/styles/theme';

const DOT_SIZE = 8;
const BOUNCE_HEIGHT = -4;
const RISE_DURATION = 420;
const FALL_DURATION = 560;
const REST_DURATION = 220;
const STAGGER_DELAY = 240;
const MIN_OPACITY = 0.55;
const MAX_OPACITY = 0.95;
const DOT_KEYS = ['dot-1', 'dot-2', 'dot-3'];
const DOT_COUNT = 3;

const TypingIndicator = () => {
  const animatedValues = useRef(
    Array.from({ length: DOT_COUNT }, () => new Animated.Value(0)),
  ).current;

  useEffect(() => {
    const startTimers: Array<ReturnType<typeof setTimeout>> = [];
    const loops: Animated.CompositeAnimation[] = [];

    animatedValues.forEach((animatedValue, index) => {
      animatedValue.setValue(0);

      const loop = Animated.loop(
        Animated.sequence([
          Animated.timing(animatedValue, {
            toValue: 1,
            duration: RISE_DURATION,
            easing: Easing.out(Easing.sin),
            useNativeDriver: true,
          }),
          Animated.timing(animatedValue, {
            toValue: 0,
            duration: FALL_DURATION,
            easing: Easing.inOut(Easing.sin),
            useNativeDriver: true,
          }),
          Animated.delay(REST_DURATION),
        ]),
        { resetBeforeIteration: true },
      );

      loops.push(loop);

      const timer = setTimeout(() => {
        loop.start();
      }, index * STAGGER_DELAY);

      startTimers.push(timer);
    });

    return () => {
      startTimers.forEach((timer) => {
        clearTimeout(timer);
      });

      loops.forEach((loop) => {
        loop.stop();
      });
    };
  }, [animatedValues]);

  return (
    <View style={styles.container} testID="typingIndicator">
      {animatedValues.map((animatedValue, index) => (
        <Animated.View
          key={DOT_KEYS[index]}
          style={[
            styles.dot,
            {
              transform: [
                {
                  translateY: animatedValue.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, BOUNCE_HEIGHT],
                  }),
                },
              ],
              opacity: animatedValue.interpolate({
                inputRange: [0, 1],
                outputRange: [MIN_OPACITY, MAX_OPACITY],
              }),
            },
          ]}
        />
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    height: 20,
    justifyContent: 'center',
  },
  dot: {
    backgroundColor: theme.color.background.primary,
    borderRadius: DOT_SIZE / 2,
    height: DOT_SIZE,
    width: DOT_SIZE,
  },
});

export default TypingIndicator;
